import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { productInfo } from "@/data/productInfo";

interface ProductInfoPanelProps {
  productId: string;
  variant?: "default" | "bubble";
}

type PanelKey = "ingredients" | "nutrition" | null;

const ProductInfoPanel = ({ productId, variant = "default" }: ProductInfoPanelProps) => {
  const [open, setOpen] = useState<PanelKey>(null);
  const info = productInfo[productId];

  if (!info) return null;

  const wrapperClass =
    variant === "bubble"
      ? "rounded-xl bg-background/70 backdrop-blur-sm px-4"
      : "pt-2 border-t border-border";

  return (
    <div className={wrapperClass}>
      <InfoRow
        label="Innehållsförteckning"
        isOpen={open === "ingredients"}
        onOpenChange={(o) => setOpen(o ? "ingredients" : null)}
      >
        <p className="text-sm text-muted-foreground leading-relaxed">{info.ingredients}</p>
      </InfoRow>

      <InfoRow
        label="Näringsinnehåll"
        isOpen={open === "nutrition"}
        onOpenChange={(o) => setOpen(o ? "nutrition" : null)}
        isLast
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 font-medium">Näringsvärde</th>
                <th className="text-right py-2 font-medium">1 brustablett</th>
                <th className="text-right py-2 font-medium">DRI* (%)</th>
              </tr>
            </thead>
            <tbody>
              {info.nutrition.map((row) => (
                <tr key={row.name} className="border-b border-border/50 last:border-0">
                  <td className="py-2 text-foreground">{row.name}</td>
                  <td className="py-2 text-right text-foreground">{row.amount}</td>
                  <td className="py-2 text-right text-foreground">{row.dri}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            *DRI = Dagligt referensintag
            {info.hasNoDri && (
              <>
                <br />
                **Inget fastställt dagligt referensintag
              </>
            )}
          </p>
        </div>
      </InfoRow>
    </div>
  );
};

interface InfoRowProps {
  label: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  isLast?: boolean;
}

const InfoRow = ({ label, isOpen, onOpenChange, children, isLast }: InfoRowProps) => {
  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <div className={isLast ? "" : "border-b border-border"}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between py-3 text-left group"
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-foreground">
              {label}
            </span>
            {isOpen ? (
              <Minus className="h-4 w-4 text-foreground transition-transform duration-200" />
            ) : (
              <Plus className="h-4 w-4 text-foreground transition-transform duration-200" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent
          className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up"
        >
          <div className="pb-4 pt-1">{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default ProductInfoPanel;
